import React from "react";
import { GPUInstanceType } from "types/InstanceTypes";
import EditOverlay from "./RackCard/EditOverlay";
import { GetCloudName, GetProviderName } from "types/InstanceTypes";
import { formatMemoryForDisplay } from "../utils/index";
import { displayPlatformType } from "components/Environment/utils";
import { FreeTierPriceWithTooltip } from "components/FreeTier";
import { getFilteredBasePriceAmount, withServiceCharge } from "utils/freeTierUtils";

import { classNames } from "components/utils";
import { motion } from "framer-motion";
import { Button } from "components/Graphs/Button";
import { CloudIcon } from "lucide-react";
import CloudDisplay from "components/Graphs/CloudDisplay";
import RackCardLabel from "./RackCard/RackCardLabel";
import PowerButton from "./RackCard/PowerButton";
import CannotStopWarning from "./RackCard/CannotStopWarning";
import EstimatedStartTimeBadge from "./RackCard/EstimatedStartTimeBadge";
import DiagonalRackPattern from "./RackCard/DiagonalRackPattern";
import CpuCountLabel from "./RackCard/CpuCountLabel";
import { getInstanceTypeArchitectures } from "utils/architecture";
import { CpuChipIcon } from "@heroicons/react/24/solid";
import { formatFixedStorage } from "./RackCard/fixedStorage";
import FixedStorageDescription from "./RackCard/FixedStorageDescription";

export interface CpuInstanceCardProps {
  instance: GPUInstanceType;
  selected?: boolean;
  onSelect: () => void;
  isFreeTier?: boolean;
  flexiblePortControlOnly: boolean;
  flexibleStorageOnly: boolean;
  hidePrice?: boolean;
  pickerAllowedLocations?: Set<string> | null;
}

const cardStyles = {
  container: {
    background: `
      repeating-linear-gradient(0deg, #1a1a1a 0px, #1a1a1a 1px, #2a2a2a 1px, #2a2a2a 2px),
      repeating-linear-gradient(90deg, #1a1a1a 0px, #1a1a1a 1px, #2a2a2a 1px, #2a2a2a 2px)
    `,
    boxShadow: `
      0 20px 40px rgba(0, 0, 0, 0.6),
      inset 0 0 20px rgba(0, 0, 0, 0.5),
      inset 0 1px 0 rgba(255, 255, 255, 0.1)
    `,
  },
  text: "text-primary",
  cloudText: "text-secondary",
  dotColor: "text-primary",
};

const CpuInstanceCard: React.FC<CpuInstanceCardProps> = ({
  instance,
  selected = false,
  onSelect,
  isFreeTier = false,
  flexiblePortControlOnly,
  flexibleStorageOnly,
  hidePrice = false,
  pickerAllowedLocations,
}) => {
  const warningPortsVisible = !flexibleStorageOnly || !flexiblePortControlOnly;

  const fixedStorage = formatFixedStorage(instance.supported_storage ?? []);

  const warningPorts = (
    <>
      {!flexibleStorageOnly && (
        <div className="flex gap-3">
          {instance.elastic_root_volume ? (
            <RackCardLabel
              showLight
              key="storage-badge-flexible"
              color="green"
              style="light"
              label="Flexible storage"
              tooltip="Configure any amount of storage when creating the instance"
            />
          ) : fixedStorage ? (
            <RackCardLabel
              showLight
              key="storage-badge-fixed"
              color="orange"
              style="light"
              label={
                <FixedStorageDescription description={fixedStorage} />
              }
              tooltip={`Storage is fixed at ${fixedStorage} by the cloud provider.`}
            />
          ) : null}
        </div>
      )}
      {!flexiblePortControlOnly && (
        <RackCardLabel
          showLight
          label={
            instance.can_modify_firewall_rules
              ? "Flexible ports"
              : "Fixed ports"
          }
          color={
            instance.can_modify_firewall_rules ? "green" : "orange"
          }
          style="light"
          tooltip={
            instance.can_modify_firewall_rules
              ? "Configure custom firewall rules and open/close ports anytime"
              : "Firewall rules and ports cannot be modified"
          }
        />
      )}
    </>
  );

  const startStopInfo = (
    <div className="flex flex-row justify-end gap-2 items-center">
      {instance.estimated_deploy_time && (
        <EstimatedStartTimeBadge
          estimatedStartTime={instance.estimated_deploy_time}
        />
      )}
      {instance.stoppable ? (
        <PowerButton />
      ) : (
        <CannotStopWarning />
      )}
    </div>
  );

  const ramSize = formatMemoryForDisplay(String(instance.memory));
  const architectures = getInstanceTypeArchitectures(instance);
  const cloudProvider =
    displayPlatformType(GetProviderName(instance) || "unknown");
  const cloud = GetCloudName(instance);
  const cloudLabel =
    GetProviderName(instance)?.toLowerCase() === "testkube"
      ? instance.type
      : displayPlatformType(cloud || "");
  const price =
    !hidePrice && instance.base_price
      ? withServiceCharge(
          getFilteredBasePriceAmount(instance, pickerAllowedLocations),
        ).toFixed(2)
      : "";

  const borderClass = selected
    ? "border-zinc-800"
    : "border-zinc-800 hover:shadow-xl";

  return (
    <Button
      variant="noshow"
      onClick={onSelect}
      className="w-full hover:scale-[1.02] transition-transform duration-200 ease-out hover:outline-2 hover:outline-highlight"
    >
      <motion.div
        className={classNames(
          "group w-full p-4 rounded-md border-2 transition-shadow duration-200 relative overflow-hidden",
          borderClass
        )}
        style={cardStyles.container}
      >
        <div className="flex flex-row h-full justify-between gap-4 relative z-10">
          <div className="flex flex-col items-center gap-2 md:justify-center flex-none">
            <div className="pt-0.5 flex-none">
              <CpuCountLabel count={instance.vcpu} />
            </div>
          </div>
          <div className="flex flex-col gap-3 justify-between flex-1 justify-items-stretch">
            <div className="flex flex-col">
              <div className="flex flex-row gap-2 items-center justify-between">
                <div
                  className={classNames(
                    "text-[17px] opacity-80 flex flex-row gap-2 items-center",
                    cardStyles.text
                  )}
                >
                  <span
                    className={classNames(
                      "flex items-center",
                      cardStyles.text
                    )}
                  >
                    {ramSize} RAM • {instance.vcpu} vCPUs
                  </span>
                </div>
                <div className="flex flex-row gap-4 items-center">
                  <div className="flex flex-row gap-2 items-center">
                    <div className="flex-row gap-2 items-center hidden sm:flex">
                      <CloudDisplay
                        icon={<CloudIcon className="h-4 w-4" />}
                        provider={cloudProvider}
                        cloud={cloudLabel}
                        className={classNames(
                          "text-sm",
                          cardStyles.cloudText
                        )}
                      />
                      {!!price && (
                        <span
                          className={classNames(
                            "opacity-25",
                            cardStyles.dotColor
                          )}
                        >
                          ●
                        </span>
                      )}
                    </div>
                    <div>
                      {price && (
                        <div
                          className={classNames(
                            "block text-[17px] font-semibold text-right self-end flex-none",
                            cardStyles.text
                          )}
                        >
                          {isFreeTier ? (
                            <FreeTierPriceWithTooltip price={price} />
                          ) : (
                            <>${price}/hr</>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-row gap-2 justify-between flex-1 items-stretch">
              <div className="flex flex-col gap-2 justify-center">
                {architectures.length > 0 && (
                  <RackCardLabel
                    label={architectures.join(" / ")}
                    icon={<CpuChipIcon className="h-4 w-4" />}
                    style="light"
                  />
                )}
                <div className="flex flex-col sm:hidden gap-2 items-start">
                  {warningPorts}
                </div>
              </div>
              <div className="flex flex-row items-stretch justify-center flex-1">
                <DiagonalRackPattern />
              </div>
              {warningPortsVisible && (
                <div className="hidden sm:flex flex-col items-stretch justify-center flex-0 gap-1 md:gap-1.5 lg:flex lg:flex-row">
                  {warningPorts}
                </div>
              )}
              <div
                className={classNames(
                  "flex-0 hidden xl:flex",
                  !warningPortsVisible ? "md:flex" : ""
                )}
              >
                {startStopInfo}
              </div>
            </div>
            <div
              className={classNames(
                "flex flex-row items-center justify-end xl:hidden",
                !warningPortsVisible ? "md:hidden" : ""
              )}
            >
              {startStopInfo}
            </div>
          </div>
        </div>
        {selected && <EditOverlay />}
      </motion.div>
    </Button>
  );
};

export default CpuInstanceCard;
